import * as Analytics from 'analytics/analytics'
import { offerPageCheckAvailabilityEvent } from 'analytics/snowplow/events'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions, initialOfferPageRooms } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useLPCUniqueOptions from 'hooks/OfferPage/useLPCUniqueOptions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useQueryParams from 'hooks/useQueryParams'
import useToggle from 'hooks/useToggle'
import { partitionBy, take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { arePricesLoading, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileDetails from 'components/AccommodationKit/AccommodationRoomTileDetails'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import AlwaysOnRateOption from './AlwaysOnRateOption'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

const RateSection = styled.div`
  > * {
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const ShowMoreContent = styled(BodyText)`
  padding: ${rem(12)};
`

interface Props {
  offer: App.Offer
  packageViews: Array<App.OfferPackageOptionView>
  checkIn?: string
  rooms: Array<OfferPageRoom>
  flightDestination?: App.Airport
  previewMode: boolean
  isHighlighted?: boolean
  onViewPackageOptions: () => void
  roomRateId?: string
  pricesLoading?: boolean
  duration?: number
  availableForPurchase?: boolean
}

function AlwaysOnRoomOption(props: Props) {
  const {
    offer,
    packageViews,
    checkIn,
    rooms,
    flightDestination,
    previewMode,
    isHighlighted,
    onViewPackageOptions,
    roomRateId,
    pricesLoading,
    duration,
    availableForPurchase,
  } = props

  const intl = useIntl()
  const utmContent = useQueryParams().get('utm_content')

  const pkg = packageViews[0].package
  const [showingMoreRates, showMoreRates] = useToggle()
  const showLessLabel = intl.formatMessage({
    id: 'Uf5ykG9emo',
    defaultMessage: 'Show less options',
    description: 'Toggle button label to collapse rate options list',
  })
  const showMoreLabel = intl.formatMessage({
    id: '+PoF/cwRV3',
    defaultMessage: 'Show more options',
    description: 'Toggle button label to expand rate options list',
  })
  const offerPageDispatch = useOfferPageDispatch()
  const initialRatesToShow = isHighlighted ? 1 : 3

  const [roomFitsCapacity, datesAvailable] = useMemo(() => [
    packageViews.some(view => view.capacityValid),
    packageViews.some(view => view.hotelAvailable),
  ], [packageViews])

  const availablePackageViews = useMemo(() => {
    const requiresRates = offer.property?.useDynamicCancellationPolicies && !!checkIn && !pricesLoading
    return packageViews.filter(view => view.capacityValid && view.hotelAvailable && (!requiresRates || view.hasAvailableRates))
  }, [checkIn, offer, packageViews, pricesLoading])

  /**
   * Preview is used only by content people.
   * We don't want to show unavailable rateOptions to the user.
   */
  const options = previewMode ? packageViews : availablePackageViews
  const availableOptions = useLPCUniqueOptions(
    offer.id,
    options,
    {
      useDynamicCancellationPolicies: offer.property?.useDynamicCancellationPolicies ?? false,
      duration,
      checkIn,
      rooms: rooms ?? initialOfferPageRooms,
    },
  )

  const { rateOptionList, isRoomRateUnmatched } = useMemo(() => {
    let isRoomRateUnmatched = false
    let rateOptionList = availableOptions

    if (isHighlighted && roomRateId && availableOptions.length >= 1) {
      /**
        * Modifying the rateOption array so we have the highlightedRateOption on top
       **/
      const [option, otherOptions] = partitionBy(availableOptions, view => view.package.roomRate?.id === roomRateId)
      if (option[0]) {
        rateOptionList = [option[0], ...otherOptions]
      } else if (utmContent == 'gha-lpc') {
        // if roomRateId is associated with user currency (eg: cb4ebd70-33d1-11ed-9609-ffb70b8fec99_USD ) remove the currency and extract the rate id for comparison
        const baseRateId = roomRateId?.split('_')[0]
        const [option, otherOptions] = partitionBy(availableOptions, view => view.package.roomRate?.id === baseRateId)
        if (option[0]) {
          rateOptionList = [option[0], ...otherOptions]
        } else {
          isRoomRateUnmatched = true
        }
      }
    }

    return { rateOptionList, isRoomRateUnmatched }
  }, [isHighlighted, roomRateId, availableOptions, utmContent])

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // eslint-disable-line react-hooks/exhaustive-deps

  const checkAvailability = !checkIn || !hasSelectedRooms(rooms)

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      if (!offer.property?.useDynamicOccupancy) {
        if (!datesAvailable) return 'sold-out-on-dates'
        if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        if (checkAvailability) {
          if (roomFitsCapacity && datesAvailable) return 'available-on-dates'
          if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
        }
      }
      if (offer.property?.useDynamicOccupancy) {
        if (!checkIn) return 'available-on-dates'
        if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
      }
    }
  }, [availableForPurchase, checkAvailability, checkIn, datesAvailable, offer, roomFitsCapacity])

  const stayPayTier = useMemo(() => {
    return packageViews.map(view => {
      return view.hotelAvailable && view.package?.stayPay?.find(tier => tier.stayNights === duration && tier.freeNights > 0)
    }).find(Boolean)
  }, [packageViews, duration])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, pkg.roomType])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback(() => {
    toggleFilter(hasSelectedRooms(rooms) ? 'checkin' : 'capacity')
    Analytics.trackEvent(offerPageCheckAvailabilityEvent({ offer, roomRateId: pkg.roomRate?.id }))
  }, [rooms, toggleFilter, offer, pkg.roomRate?.id])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  if (!pkg.roomType) {
    return null
  }

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner kind="promotional-primary" icon={<LineInfoCircleIcon />}>
        {/* eslint-disable-next-line formatjs/no-multiple-plurals */}
        <FormattedMessage
          id="UK4qcBuBzJ"
          defaultMessage="<b>Special offer:</b> You get {freeNights, plural, one {# night} other {# nights}} free when staying {stayNights, plural, one {# night} other {# nights}} on select packages."
          description="Promotional banner about free nights for longer stays on select packages"
          values={{
            b: (chunks) => <b>{chunks}</b>,
            freeNights: stayPayTier.freeNights,
            stayNights: stayPayTier.stayNights,
          }}
        />
      </MessageBanner>}
      <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
        {isHighlighted && <MessageBanner kind="promotional-primary" icon={!isRoomRateUnmatched && <LineSightseeingIcon />}>
          <b>{intl.formatMessage({
            id: 'EUmtBJkv1K',
            defaultMessage: 'You were interested in this option',
            description: 'Highlighted banner text for a room option the user previously viewed',
          })}
          </b>
        </MessageBanner>}
        <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
        <AccommodationRoomTile roomPackage={pkg}>
          <AccommodationRoomTileImage
            roomPackage={pkg}
          />
          <AccommodationRoomTileDetails
            roomPackage={pkg}
            urgencyLabels={<>
              <BookingsCountLabel bookings={pkg.roomType.stats.booked} threshold="room" />
              {checkIn && <RoomsRemainingLabel roomCount={packageViews[0].availableRooms ?? 0} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={pkg.roomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          >
            <AccommodationRoomTileActions
              roomPackage={pkg}
              state={actionsState}
              isWalled={false}
              onChangeOccupancyClick={onChangeOccupancyClick}
              onChangeDatesClick={handleChangeDatesClick}
              onCheckAvailabilityClick={handleCheckAvailabilityClick}
            />
          </AccommodationRoomTileDetails>
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          {take(rateOptionList, showingMoreRates ? 100 : initialRatesToShow).map((view, index) => <AlwaysOnRateOption
            key={view.package.uniqueKey}
            offer={offer}
            optionNumber={index + 1}
            packageView={view}
            flightDestination={flightDestination}
            showOptionLabel={rateOptionList.length > 1}
          />)}
          {rateOptionList.length > initialRatesToShow &&
            <ShowMoreContent variant="medium" align="center">
              <SmallToggleButton
                on={showingMoreRates}
                onClick={showMoreRates}
              >
                {showingMoreRates ? showLessLabel : showMoreLabel}
              </SmallToggleButton>
            </ShowMoreContent>}
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState: App.State) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  duration: state.duration,
  previewMode: appState.system.previewMode,
  roomRateId: state.roomOption?.roomRateId,
  pricesLoading: arePricesLoading(appState, state),
  availableForPurchase: isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), AlwaysOnRoomOption, preventRenderWithOpenFilter)
